import type { BreakSeverity } from '@/features/manager-breaks-demo/types/breaks';
import type { ProjectProcedure } from '../types/project';

interface ProceduresColumnProps {
  procedures: ProjectProcedure[];
  /** Followed procedures are quiet reassurance; broken ones carry the risk. */
  variant: 'followed' | 'broken';
  /**
   * Broken only - how many off-book procedures were hidden because they're
   * already surfaced live in "Breaking now". Shown as a footnote so the two
   * columns never repeat yet the manager still sees the full count.
   */
  alsoLiveCount?: number;
}

/** Compliance-bar fill on a broken procedure, by how badly it bites. */
const BROKEN_FILL: Record<BreakSeverity, string> = {
  high: 'bg-rose-500',
  medium: 'bg-amber-400',
  low: 'bg-zinc-300',
};

/** Left rail on a broken procedure, by severity. */
const BROKEN_RAIL: Record<BreakSeverity, string> = {
  high: 'bg-rose-500',
  medium: 'bg-amber-400',
  low: 'bg-zinc-300',
};

/** Trend chip tone on a broken procedure, by severity. */
const BROKEN_TREND: Record<BreakSeverity, string> = {
  high: 'bg-rose-50 text-rose-600',
  medium: 'bg-amber-50 text-amber-700',
  low: 'bg-zinc-100 text-zinc-500',
};

/** A slim adherence bar - the policy-health read, shared by both variants. */
function ComplianceBar({ pct, fillClass }: { pct: number; fillClass: string }) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-zinc-100">
      <div
        className={`h-full rounded-full ${fillClass}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

/** Column header - count chip tone tracks whether the news is good or bad. */
function ColumnHeader({
  title,
  count,
  variant,
}: {
  title: string;
  count: number;
  variant: 'followed' | 'broken';
}) {
  const isBroken = variant === 'broken';
  return (
    <div className="mb-4 flex items-center justify-between">
      <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.14em] text-zinc-500">
        <span
          className={`flex h-5 w-5 items-center justify-center rounded-full ${
            isBroken
              ? 'bg-rose-50 text-rose-600'
              : 'bg-emerald-50 text-emerald-600'
          }`}
        >
          {isBroken ? (
            <svg
              viewBox="0 0 24 24"
              className="h-3 w-3"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          ) : (
            <svg
              viewBox="0 0 24 24"
              className="h-3 w-3"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M5 13l4 4L19 7" />
            </svg>
          )}
        </span>
        {title}
      </h3>
      <span className="rounded-lg bg-zinc-100 px-2 py-0.5 text-xs font-semibold text-zinc-600">
        {count}
      </span>
    </div>
  );
}

/** Footnote tying the column back to "Breaking now" without repeating the items. */
function AlsoLiveNote({ count }: { count: number }) {
  return (
    <p className="mt-3 flex items-center gap-1.5 px-1 text-xs text-zinc-500">
      <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
      {count} more {count === 1 ? 'rule is' : 'rules are'} already breaking live
      — see <span className="font-medium text-zinc-700">Breaking now</span>.
    </p>
  );
}

/**
 * The procedures (playbooks) a project is or isn't holding to - the policy-health
 * view, not a second incident list. Each rule shows its adherence over time and
 * a trend. The off-book column is de-duped against "Breaking now", so it surfaces
 * only the silent, not-yet-live violations.
 */
export function ProceduresColumn({
  procedures,
  variant,
  alsoLiveCount = 0,
}: ProceduresColumnProps) {
  const isBroken = variant === 'broken';

  return (
    <div>
      <ColumnHeader
        title={isBroken ? 'Procedures off-book' : 'Procedures followed'}
        count={procedures.length}
        variant={variant}
      />

      {isBroken && procedures.length === 0 ? (
        <>
          <div className="flex items-center gap-3 rounded-2xl border border-emerald-100 bg-emerald-50/40 p-5 text-sm text-emerald-700">
            <svg
              viewBox="0 0 24 24"
              className="h-5 w-5 shrink-0"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M5 13l4 4L19 7" />
            </svg>
            {alsoLiveCount > 0
              ? 'No silent drift — every off-book rule is already breaking live.'
              : 'Every procedure is being followed.'}
          </div>
          {alsoLiveCount > 0 && <AlsoLiveNote count={alsoLiveCount} />}
        </>
      ) : isBroken ? (
        <>
          <div className="flex flex-col gap-3">
            {procedures.map((procedure) => {
              const severity = procedure.severity ?? 'medium';
              return (
                <div
                  key={procedure.id}
                  className="relative overflow-hidden rounded-2xl border border-zinc-100 bg-white p-5 shadow-[0_18px_44px_-30px_rgba(16,27,58,0.45)]"
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none absolute inset-y-4 start-0 w-1 rounded-full ${BROKEN_RAIL[severity]}`}
                  />
                  <div className="ps-2">
                    <h4 className="text-sm font-semibold leading-snug text-black">
                      {procedure.name}
                    </h4>
                    <p className="mt-1.5 text-xs leading-relaxed text-zinc-600">
                      {procedure.summary}
                    </p>

                    <div className="mt-3.5">
                      <div className="mb-1.5 flex items-center justify-between gap-2">
                        <span className="text-xs font-semibold text-zinc-700">
                          {procedure.complianceLabel}
                        </span>
                        <span
                          className={`rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${BROKEN_TREND[severity]}`}
                        >
                          {procedure.trendLabel}
                        </span>
                      </div>
                      <ComplianceBar
                        pct={procedure.compliancePct}
                        fillClass={BROKEN_FILL[severity]}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {alsoLiveCount > 0 && <AlsoLiveNote count={alsoLiveCount} />}
        </>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-zinc-100 bg-white shadow-[0_18px_44px_-32px_rgba(16,27,58,0.4)]">
          {procedures.map((procedure, procedureIndex) => (
            <div
              key={procedure.id}
              className={`relative p-4 ${
                procedureIndex > 0 ? 'border-t border-zinc-100' : ''
              }`}
            >
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-3 start-0 w-1 rounded-full bg-emerald-400/70"
              />
              <div className="flex items-start gap-3 ps-2">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                  <svg
                    viewBox="0 0 24 24"
                    className="h-3 w-3"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="text-sm font-medium leading-snug text-zinc-800">
                      {procedure.name}
                    </h4>
                    <span className="shrink-0 text-[11px] font-medium text-zinc-400">
                      {procedure.complianceLabel}
                    </span>
                  </div>
                  <p className="mt-1.5 text-[11px] font-medium uppercase tracking-wide text-emerald-600/80">
                    {procedure.trendLabel}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
